import _defineProperty from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.28.4/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getGlobalSingleton } from '../carrier.js';
import { _getTraceInfoFromScope } from '../client.js';
import { getClient, getCurrentScope, getGlobalScope, getIsolationScope } from '../currentScopes.js';
import { DEBUG_BUILD } from '../debug-build.js';
import { mergeScopeData } from '../utils/applyScopeDataToEvent.js';
import { debug, consoleSandbox } from '../utils/debug-logger.js';
import { isParameterizedString } from '../utils/is.js';
import { _getSpanForScope } from '../utils/spanOnScope.js';
import { timestampInSeconds } from '../utils/time.js';
import { SEVERITY_TEXT_TO_SEVERITY_NUMBER } from './constants.js';
import { createLogEnvelope } from './envelope.js';
var MAX_LOG_BUFFER_SIZE = 100;

/**
 * Converts a log attribute to a serialized log attribute.
 *
 * @param key - The key of the log attribute.
 * @param value - The value of the log attribute.
 * @returns The serialized log attribute.
 */
function logAttributeToSerializedLogAttribute(value) {
  switch (typeof value) {
    case 'number':
      if (Number.isInteger(value)) {
        return {
          value: value,
          type: 'integer'
        };
      }
      return {
        value: value,
        type: 'double'
      };
    case 'boolean':
      return {
        value: value,
        type: 'boolean'
      };
    case 'string':
      return {
        value: value,
        type: 'string'
      };
    default:
      {
        var stringValue = '';
        try {
          var _JSON$stringify;
          stringValue = (_JSON$stringify = JSON.stringify(value)) !== null && _JSON$stringify !== void 0 ? _JSON$stringify : '';
        } catch (_unused) {
          // Do nothing
        }
        return {
          value: stringValue,
          type: 'string'
        };
      }
  }
}

/**
 * Sets a log attribute if the value exists and the attribute key is not already present.
 *
 * @param logAttributes - The log attributes object to modify.
 * @param key - The attribute key to set.
 * @param value - The value to set (only sets if truthy and key not present).
 * @param setEvenIfPresent - Whether to set the attribute if it is present. Defaults to true.
 */
function setLogAttribute(logAttributes, key, value) {
  var setEvenIfPresent = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  if (value && (!logAttributes[key] || setEvenIfPresent)) {
    logAttributes[key] = value;
  }
}

/**
 * Captures a serialized log event and adds it to the log buffer for the given client.
 *
 * @param client - A client. Uses the current client if not provided.
 * @param serializedLog - The serialized log event to capture.
 *
 * @experimental This method will experience breaking changes. This is not yet part of
 * the stable Sentry SDK API and can be changed or removed without warning.
 */
function _INTERNAL_captureSerializedLog(client, serializedLog) {
  var bufferMap = _getBufferMap();
  var logBuffer = _INTERNAL_getLogBuffer(client);
  if (logBuffer === undefined) {
    bufferMap.set(client, [serializedLog]);
  } else {
    bufferMap.set(client, [].concat(_toConsumableArray(logBuffer), [serializedLog]));
    if (logBuffer.length >= MAX_LOG_BUFFER_SIZE) {
      _INTERNAL_flushLogsBuffer(client, logBuffer);
    }
  }
}

/**
 * Captures a log event and sends it to Sentry.
 *
 * @param log - The log event to capture.
 * @param scope - A scope. Uses the current scope if not provided.
 * @param client - A client. Uses the current client if not provided.
 * @param captureSerializedLog - A function to capture the serialized log.
 *
 * @experimental This method will experience breaking changes. This is not yet part of
 * the stable Sentry SDK API and can be changed or removed without warning.
 */
function _INTERNAL_captureLog(beforeLog) {
  var _client$getSdkMetadat, _client$getSdkMetadat2;
  var client = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : getClient();
  var currentScope = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : getCurrentScope();
  var captureSerializedLog = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : _INTERNAL_captureSerializedLog;
  if (!client) {
    DEBUG_BUILD && debug.warn('No client available to capture log.');
    return;
  }
  var _client$getOptions = client.getOptions(),
    release = _client$getOptions.release,
    environment = _client$getOptions.environment,
    enableLogs = _client$getOptions.enableLogs,
    beforeSendLog = _client$getOptions.beforeSendLog,
    _experiments = _client$getOptions._experiments;
  // eslint-disable-next-line deprecation/deprecation
  var shouldEnableLogs = enableLogs !== null && enableLogs !== void 0 ? enableLogs : _experiments === null || _experiments === void 0 ? void 0 : _experiments.enableLogs;
  if (!shouldEnableLogs) {
    DEBUG_BUILD && debug.warn('logging option not enabled, log will not be captured.');
    return;
  }
  // eslint-disable-next-line deprecation/deprecation
  var actualBeforeSendLog = beforeSendLog !== null && beforeSendLog !== void 0 ? beforeSendLog : _experiments === null || _experiments === void 0 ? void 0 : _experiments.beforeSendLog;
  var _getTraceInfoFromScop = _getTraceInfoFromScope(client, currentScope),
    _getTraceInfoFromScop2 = _slicedToArray(_getTraceInfoFromScop, 2),
    traceContext = _getTraceInfoFromScop2[1];
  var processedLogAttributes = _objectSpread({}, beforeLog.attributes);
  var _getMergedScopeData = getMergedScopeData(currentScope),
    _getMergedScopeData$u = _getMergedScopeData.user,
    id = _getMergedScopeData$u.id,
    email = _getMergedScopeData$u.email,
    username = _getMergedScopeData$u.username;
  setLogAttribute(processedLogAttributes, 'user.id', id, false);
  setLogAttribute(processedLogAttributes, 'user.email', email, false);
  setLogAttribute(processedLogAttributes, 'user.name', username, false);
  setLogAttribute(processedLogAttributes, 'sentry.release', release);
  setLogAttribute(processedLogAttributes, 'sentry.environment', environment);
  var _ref = (_client$getSdkMetadat = (_client$getSdkMetadat2 = client.getSdkMetadata()) === null || _client$getSdkMetadat2 === void 0 ? void 0 : _client$getSdkMetadat2.sdk) !== null && _client$getSdkMetadat !== void 0 ? _client$getSdkMetadat : {},
    name = _ref.name,
    version = _ref.version;
  setLogAttribute(processedLogAttributes, 'sentry.sdk.name', name);
  setLogAttribute(processedLogAttributes, 'sentry.sdk.version', version);
  var beforeLogMessage = beforeLog.message;
  if (isParameterizedString(beforeLogMessage)) {
    var __sentry_template_string__ = beforeLogMessage.__sentry_template_string__,
      _beforeLogMessage$__s = beforeLogMessage.__sentry_template_values__,
      __sentry_template_values__ = _beforeLogMessage$__s === void 0 ? [] : _beforeLogMessage$__s;
    processedLogAttributes['sentry.message.template'] = __sentry_template_string__;
    __sentry_template_values__.forEach(function (param, index) {
      processedLogAttributes["sentry.message.parameter.".concat(index)] = param;
    });
  }
  var span = _getSpanForScope(currentScope);
  // Add the parent span ID to the log attributes for trace context
  setLogAttribute(processedLogAttributes, 'sentry.trace.parent_span_id', span === null || span === void 0 ? void 0 : span.spanContext().spanId);
  var processedLog = _objectSpread(_objectSpread({}, beforeLog), {}, {
    attributes: processedLogAttributes
  });
  client.emit('beforeCaptureLog', processedLog);

  // We need to wrap this in `consoleSandbox` to avoid recursive calls to `beforeSendLog`
  var log = actualBeforeSendLog ? consoleSandbox(function () {
    return actualBeforeSendLog(processedLog);
  }) : processedLog;
  if (!log) {
    client.recordDroppedEvent('before_send', 'log_item', 1);
    DEBUG_BUILD && debug.warn('beforeSendLog returned null, log will not be captured.');
    return;
  }
  var level = log.level,
    message = log.message,
    _log$attributes = log.attributes,
    attributes = _log$attributes === void 0 ? {} : _log$attributes,
    severityNumber = log.severityNumber;
  var serializedLog = {
    timestamp: timestampInSeconds(),
    level: level,
    body: message,
    trace_id: traceContext === null || traceContext === void 0 ? void 0 : traceContext.trace_id,
    severity_number: severityNumber !== null && severityNumber !== void 0 ? severityNumber : SEVERITY_TEXT_TO_SEVERITY_NUMBER[level],
    attributes: Object.keys(attributes).reduce(function (acc, key) {
      acc[key] = logAttributeToSerializedLogAttribute(attributes[key]);
      return acc;
    }, {})
  };
  captureSerializedLog(client, serializedLog);
  client.emit('afterCaptureLog', log);
}

/**
 * Flushes the logs buffer to Sentry.
 *
 * @param client - A client.
 * @param maybeLogBuffer - A log buffer. Uses the log buffer for the given client if not provided.
 *
 * @experimental This method will experience breaking changes. This is not yet part of
 * the stable Sentry SDK API and can be changed or removed without warning.
 */
function _INTERNAL_flushLogsBuffer(client, maybeLogBuffer) {
  var _ref2;
  var logBuffer = (_ref2 = maybeLogBuffer !== null && maybeLogBuffer !== void 0 ? maybeLogBuffer : _INTERNAL_getLogBuffer(client)) !== null && _ref2 !== void 0 ? _ref2 : [];
  if (logBuffer.length === 0) {
    return;
  }
  var clientOptions = client.getOptions();
  var envelope = createLogEnvelope(logBuffer, clientOptions._metadata, clientOptions.tunnel, client.getDsn());

  // Clear the log buffer after envelopes have been constructed.
  _getBufferMap().set(client, []);
  client.emit('flushLogs');

  // sendEnvelope should not throw
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  client.sendEnvelope(envelope);
}

/**
 * Returns the log buffer for a given client.
 *
 * Exported for testing purposes.
 *
 * @param client - The client to get the log buffer for.
 * @returns The log buffer for the given client.
 */
function _INTERNAL_getLogBuffer(client) {
  return _getBufferMap().get(client);
}

/**
 * Get the scope data for the current scope after merging with the
 * global scope and isolation scope.
 *
 * @param currentScope - The current scope.
 * @returns The scope data.
 */
function getMergedScopeData(currentScope) {
  var scopeData = getGlobalScope().getScopeData();
  mergeScopeData(scopeData, getIsolationScope().getScopeData());
  mergeScopeData(scopeData, currentScope.getScopeData());
  return scopeData;
}
function _getBufferMap() {
  // The reference to the Client <> LogBuffer map is stored on the carrier to ensure it's always the same
  return getGlobalSingleton('clientToLogBufferMap', function () {
    return new WeakMap();
  });
}
export { _INTERNAL_captureLog, _INTERNAL_captureSerializedLog, _INTERNAL_flushLogsBuffer, _INTERNAL_getLogBuffer, logAttributeToSerializedLogAttribute };